import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Separator } from "../ui/separator";
import { Send, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "./useApi";

export function ManagerNotificationsCard({ token, t, hasApiKey }) {
  var [managerList, setManagerList] = useState([]);
  var [notificationHistory, setNotificationHistory] = useState([]);
  var [customNotification, setCustomNotification] = useState({ subject: "", message: "", notification_type: "custom", recipient_ids: [] });
  var [sendingNotification, setSendingNotification] = useState(false);
  var { fetchJson, postJson } = useApi(token);

  useEffect(function() {
    fetchJson("/api/admin/notifications/manager-list").then(function(d) { setManagerList(d.managers || []); }).catch(function() {});
    fetchJson("/api/admin/notifications/history").then(function(d) { setNotificationHistory(d.notifications || []); }).catch(function() {});
  }, []);

  async function handleSendNotification() {
    if (customNotification.notification_type === "custom" && (!customNotification.subject || !customNotification.message)) {
      toast.error(t("settings.notificationRequiredFields")); return;
    }
    setSendingNotification(true);
    try {
      var response = await postJson("/api/admin/notifications/send-to-managers", customNotification);
      if (response.ok) {
        var data = await response.json();
        toast.success(data.message || t("settings.notificationSent"));
        setCustomNotification({ subject: "", message: "", notification_type: "custom", recipient_ids: [] });
        fetchJson("/api/admin/notifications/history").then(function(d) { setNotificationHistory(d.notifications || []); }).catch(function() {});
      } else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSendingNotification(false); }
  }

  return (
    <Card className="bento-card border-orange-500/50" data-testid="manager-notifications-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-orange-500" />{t("settings.managerNotificationsTitle")}</CardTitle>
        <CardDescription>{t("settings.managerNotificationsDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
          <Mail className="w-5 h-5 text-orange-600" />
          <div>
            <p className="font-medium text-orange-800 dark:text-orange-300">{t("settings.activeManagers")}: {managerList.length}</p>
            <p className="text-sm text-orange-600 dark:text-orange-400">{t("settings.managersWillReceive")}</p>
          </div>
        </div>
        <Separator />
        <div className="space-y-4">
          <h4 className="font-medium">{t("settings.sendNotification")}</h4>
          <div>
            <Label className="form-label">{t("settings.notificationType")}</Label>
            <Select value={customNotification.notification_type} onValueChange={function(v) { setCustomNotification({ ...customNotification, notification_type: v }); }}>
              <SelectTrigger data-testid="notification-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">{t("settings.customNotification")}</SelectItem>
                <SelectItem value="payment_reminder">{t("settings.paymentReminder")}</SelectItem>
                <SelectItem value="system_update">{t("settings.systemUpdate")}</SelectItem>
                <SelectItem value="maintenance_alert">{t("settings.maintenanceAlert")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {customNotification.notification_type === "custom" && (
            <>
              <div>
                <Label className="form-label">{t("settings.notificationSubject")}</Label>
                <Input value={customNotification.subject} onChange={function(e) { setCustomNotification({ ...customNotification, subject: e.target.value }); }} placeholder={t("settings.subjectPlaceholder")} data-testid="notification-subject-input" />
              </div>
              <div>
                <Label className="form-label">{t("settings.notificationMessage")}</Label>
                <textarea className="w-full min-h-[100px] p-3 border rounded-lg bg-background text-foreground resize-y" value={customNotification.message} onChange={function(e) { setCustomNotification({ ...customNotification, message: e.target.value }); }} placeholder={t("settings.messagePlaceholder")} data-testid="notification-message-input" />
              </div>
            </>
          )}
          <div className="flex gap-3">
            <Button onClick={handleSendNotification} disabled={sendingNotification || !hasApiKey} data-testid="send-notification-btn">
              {sendingNotification ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sendingNotification ? t("settings.sending") : t("settings.sendToAllManagers")}
            </Button>
          </div>
          {!hasApiKey && <p className="text-sm text-muted-foreground">{t("settings.configureEmailFirst")}</p>}
        </div>
        <Separator />
        <div className="space-y-3">
          <h4 className="font-medium">{t("settings.notificationHistory")}</h4>
          {notificationHistory.length > 0 ? (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {notificationHistory.slice(0, 10).map(function(notif, index) {
                return (
                  <div key={notif.notification_id || index} className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">{notif.subject}</p>
                        <p className="text-xs text-muted-foreground">{t("settings.sentBy")}: {notif.sent_by_name} | {t("settings.recipients")}: {notif.sent_count}/{notif.recipient_count}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">{notif.created_at ? new Date(notif.created_at).toLocaleDateString() : ""}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-muted-foreground">{t("settings.noNotificationHistory")}</p>}
        </div>
      </CardContent>
    </Card>
  );
}
